import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { MagnifyingGlass, Plus, X, Eye, EyeSlash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { MultiSelect } from "../components/MultiSelect";
import { INDUSTRIES, PRODUCT_TYPES, TIERS, STATES, STATUSES } from "../constants/lenderOptions";

export default function LenderDirectory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || user?.role === "leader";  // both admin & leader can add/delete/hide lenders
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Multi-select filters (arrays)
  const [q, setQ] = useState("");
  const [tiers, setTiers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [restrictedIndustries, setRestrictedIndustries] = useState([]);
  const [states, setStates] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showHidden, setShowHidden] = useState(false);  // dedicated toggle to view hidden lenders

  // Numeric advanced filters
  const [dealAmount, setDealAmount] = useState("");
  const [dealTerm, setDealTerm] = useState("");
  const [maxNegDays, setMaxNegDays] = useState("");
  const [maxNsfs, setMaxNsfs] = useState("");
  const [minRevenue, setMinRevenue] = useState("");
  const [minTib, setMinTib] = useState("");
  const [minCredit, setMinCredit] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const debounceRef = useRef(null);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    tiers.forEach((t) => params.append("tier", t));
    industries.forEach((i) => params.append("industry", i));
    restrictedIndustries.forEach((i) => params.append("restricted_industry", i));
    states.forEach((s) => params.append("state", s));
    products.forEach((p) => params.append("product_type", p));
    if (showHidden) {
      // viewing hidden lenders only — overrides any other status selection
      params.append("status", "hidden");
    } else {
      statuses.forEach((s) => params.append("status", s));
    }
    if (dealAmount) params.set("deal_amount", dealAmount);
    if (dealTerm) params.set("deal_term_months", dealTerm);
    if (maxNegDays) params.set("max_neg_days_gte", maxNegDays);
    if (maxNsfs) params.set("max_nsfs_gte", maxNsfs);
    if (minRevenue) params.set("min_revenue_lte", minRevenue);
    if (minTib) params.set("min_tib_lte", minTib);
    if (minCredit) params.set("min_credit_score_lte", minCredit);
    return params;
  };

  const fetchLenders = async () => {
    setLoading(true);
    const { data } = await api.get(`/lenders?${buildParams().toString()}`);
    setItems(data || []);
    setLoading(false);
  };

  // Auto-apply on any filter change (debounced 300ms — handles fast clicking + typing)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchLenders, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, tiers, industries, restrictedIndustries, states, products, statuses, showHidden, dealAmount, dealTerm, maxNegDays, maxNsfs, minRevenue, minTib, minCredit]);

  const activeCount = useMemo(() => {
    return (
      (q ? 1 : 0) + tiers.length + industries.length + restrictedIndustries.length +
      states.length + products.length + statuses.length +
      [dealAmount, dealTerm, maxNegDays, maxNsfs, minRevenue, minTib, minCredit].filter(Boolean).length
    );
  }, [q, tiers, industries, restrictedIndustries, states, products, statuses, dealAmount, dealTerm, maxNegDays, maxNsfs, minRevenue, minTib, minCredit]);

  const clearAll = () => {
    setQ(""); setTiers([]); setIndustries([]); setRestrictedIndustries([]); setStates([]); setProducts([]); setStatuses([]);
    setDealAmount(""); setDealTerm(""); setMaxNegDays(""); setMaxNsfs("");
    setMinRevenue(""); setMinTib(""); setMinCredit("");
  };

  const restoreLender = async (lender) => {
    if (!window.confirm(`Restore "${lender.name}" to active lenders?`)) return;
    try {
      await api.post(`/lenders/${lender.id}/status`, null, { params: { status: "active" } });
      toast.success(`${lender.name} restored to active`);
      fetchLenders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  const hideLender = async (lender) => {
    if (!window.confirm(`Hide "${lender.name}" from the directory? You can restore it from the hidden view.`)) return;
    try {
      await api.post(`/lenders/${lender.id}/status`, null, { params: { status: "hidden" } });
      toast.success(`${lender.name} hidden`);
      fetchLenders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hide failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Directory</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter text-slate-900">Lender Directory</h1>
          <p className="mt-1 text-slate-600">Mix any combination of filters — they apply instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button
              variant={showHidden ? "default" : "outline"}
              onClick={() => setShowHidden((v) => !v)}
              className={showHidden ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
              data-testid="toggle-show-hidden"
            >
              {showHidden ? <EyeSlash size={14} weight="bold" className="mr-1.5" /> : <Eye size={14} weight="bold" className="mr-1.5" />}
              {showHidden ? "Showing hidden" : "Show hidden"}
            </Button>
          )}
          {canManage && (
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.lenderAddBtn}>
              <Link to="/lenders/new"><Plus size={16} weight="bold" className="mr-2" />Add lender</Link>
            </Button>
          )}
        </div>
      </div>

      {showHidden && (
        <div className="bg-slate-900 text-white rounded-lg px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="hidden-banner">
          <div className="flex items-center gap-2 text-sm">
            <EyeSlash size={16} weight="fill" className="text-amber-300" />
            <strong>Hidden lenders view</strong> · these are excluded from the main directory and lender match. Click "Restore" to add one back.
          </div>
          <button onClick={() => setShowHidden(false)} className="text-xs underline text-slate-300 hover:text-white">Exit hidden view</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <FilterLabel>Search</FilterLabel>
            <div className="relative mt-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lender name, contact, notes…" className="pl-9" data-testid={TID.lenderSearch} />
            </div>
          </div>
          <div>
            <FilterLabel>Tier</FilterLabel>
            <div className="mt-1"><MultiSelect options={TIERS} value={tiers} onChange={setTiers} testId="lender-filter-tier" /></div>
          </div>
          <div>
            <FilterLabel>Product</FilterLabel>
            <div className="mt-1"><MultiSelect options={PRODUCT_TYPES} value={products} onChange={setProducts} testId={TID.lenderFilterProduct} /></div>
          </div>
          <div>
            <FilterLabel>State</FilterLabel>
            <div className="mt-1"><MultiSelect options={STATES} value={states} onChange={setStates} searchable testId={TID.lenderFilterState} /></div>
          </div>
          <div>
            <FilterLabel>Status</FilterLabel>
            <div className="mt-1"><MultiSelect options={STATUSES} value={statuses} onChange={setStatuses} testId={TID.lenderFilterStatus} /></div>
          </div>
          <div className="md:col-span-2">
            <FilterLabel>Industry funded</FilterLabel>
            <div className="mt-1"><MultiSelect options={INDUSTRIES} value={industries} onChange={setIndustries} searchable testId={TID.lenderFilterIndustry} placeholder="Any industry" /></div>
          </div>
          <div className="md:col-span-2">
            <FilterLabel>Exclude lenders that restrict</FilterLabel>
            <div className="mt-1"><MultiSelect options={INDUSTRIES} value={restrictedIndustries} onChange={setRestrictedIndustries} searchable testId="filter-restricted-industry" placeholder="Don't restrict…" /></div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={() => setShowAdvanced((s) => !s)} className="text-slate-700" data-testid="toggle-advanced-filters" size="sm">
            {showAdvanced ? "Hide" : "Show"} advanced filters
          </Button>
          {activeCount > 0 && (
            <Button variant="ghost" onClick={clearAll} className="text-slate-600" size="sm">
              <X size={12} className="mr-1" />Clear all ({activeCount})
            </Button>
          )}
          <div className="ml-auto text-xs text-slate-500 font-mono">
            {loading ? "Loading…" : `${items.length} result${items.length !== 1 ? "s" : ""}`}
          </div>
        </div>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <NumField label="Deal amount ($)" value={dealAmount} onChange={setDealAmount} testId="filter-deal-amount" />
            <NumField label="Deal term (mo)" value={dealTerm} onChange={setDealTerm} testId="filter-deal-term" />
            <NumField label="≥ Neg days OK" value={maxNegDays} onChange={setMaxNegDays} testId="filter-neg-days" />
            <NumField label="≥ NSFs OK" value={maxNsfs} onChange={setMaxNsfs} testId="filter-nsfs" />
            <NumField label="Revenue ≥ ($)" value={minRevenue} onChange={setMinRevenue} testId="filter-revenue" />
            <NumField label="TIB ≥ (mo)" value={minTib} onChange={setMinTib} testId="filter-tib" />
            <NumField label="Credit ≥" value={minCredit} onChange={setMinCredit} testId="filter-credit" />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Lender</th>
              <th className="text-left px-4 py-3 font-semibold">Tier</th>
              <th className="text-left px-4 py-3 font-semibold">Products</th>
              <th className="text-left px-4 py-3 font-semibold">Max term</th>
              <th className="text-left px-4 py-3 font-semibold">FICO</th>
              <th className="text-left px-4 py-3 font-semibold">TIB</th>
              <th className="text-left px-4 py-3 font-semibold">Restricted</th>
              <th className="text-left px-4 py-3 font-semibold">Notes</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {canManage && <th className="text-right px-4 py-3 font-semibold w-24">Action</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={canManage ? 10 : 9} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={canManage ? 10 : 9} className="px-6 py-10 text-center text-slate-500">{showHidden ? "No hidden lenders." : "No lenders match."}</td></tr>)}
            {items.map((l) => (
              <tr key={l.id} data-testid={TID.lenderRow(l.id)} className="border-t border-slate-200 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 align-top">
                  <Link to={`/lenders/${l.id}`} className="font-semibold text-slate-900 hover:text-blue-600 inline-flex items-center gap-1.5">
                    {l.is_hot && <span title="Hot lender" className="text-base leading-none">🔥</span>}
                    {l.name}
                  </Link>
                  {l.contact_name && <div className="text-xs text-slate-500">{l.contact_name}</div>}
                </td>
                <td className="px-4 py-3"><TierBadge tier={l.tier} tiers={l.tiers} /></td>
                <td className="px-4 py-3 text-xs text-slate-700">{(l.product_types || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{l.max_term_months != null ? `${l.max_term_months} mo` : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.min_credit_score ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.min_time_in_business_months != null ? `${l.min_time_in_business_months}m` : "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600 max-w-[200px] truncate">{(l.restricted_industries || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600 max-w-[280px] truncate">{l.notes || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {l.status === "hidden" ? (
                      <button
                        onClick={() => restoreLender(l)}
                        data-testid={`restore-lender-${l.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-[0.1em] font-semibold rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      >
                        <ArrowCounterClockwise size={11} weight="bold" /> Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => hideLender(l)}
                        data-testid={`hide-lender-${l.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-[0.1em] font-semibold rounded border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 opacity-0 group-hover:opacity-100"
                        title="Hide from directory"
                      >
                        <EyeSlash size={11} weight="bold" /> Hide
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FilterLabel = ({ children }) => (
  <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{children}</label>
);

const NumField = ({ label, value, onChange, testId }) => (
  <div>
    <FilterLabel>{label}</FilterLabel>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-slate-900 focus:ring-2 focus:ring-slate-200 outline-none"
    />
  </div>
);

const TIER_COLORS = {
  "Tier 1": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Tier 2A": "bg-blue-50 text-blue-700 border-blue-200",
  "Tier 2B": "bg-sky-50 text-sky-700 border-sky-200",
  "Tier 3A": "bg-amber-50 text-amber-800 border-amber-200",
  "Tier 3B": "bg-yellow-50 text-yellow-800 border-yellow-200",
  "Tier 4": "bg-orange-50 text-orange-800 border-orange-200",
  "Reverse": "bg-purple-50 text-purple-700 border-purple-200",
  "Canada": "bg-rose-50 text-rose-700 border-rose-200",
  "SBA / MAYA": "bg-slate-100 text-slate-700 border-slate-300",
};
export function TierBadge({ tier, tiers }) {
  // Backward-compat: render single tier OR all from `tiers` array.
  const list = Array.isArray(tiers) && tiers.length ? tiers : (tier ? [tier] : []);
  if (!list.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((t) => (
        <span key={t} className={`inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${TIER_COLORS[t] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
          {t}
        </span>
      ))}
    </div>
  );
}
